import {
  FaceIcon,
  RulerHorizontalIcon,
  ComponentBooleanIcon,
  Half1Icon,
  ComponentPlaceholderIcon,
  ComponentInstanceIcon,
  CommitIcon,
  ComponentNoneIcon,
} from "@radix-ui/react-icons";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { FilterMenuSubMenu } from "./SubFilterMenu";

export type FilterMenuItem = {
  label: string;
  value: Filter;
  icon?: React.ReactNode;
};

export type ActiveFilter = {
  field: string;
  value: FilterMenuItem;
};

export const fieldToLabelMap: Record<string, string> = {
  avgSentiment: "Avg Sentiment",
  avgQaScore: "Avg QA Score",
  avgQaScoreRoundRange: "Avg QA Score",
  avgQuackMessageScoreRoundRange: "Avg Quack Message Score",
  category: "Category",
  topic: "Topic",
  subTopic: "Intent",
  agentAssigned: "Agent",
  team: "Team",
  status: "Status",
  channel: "Channel",
};

export const fieldToIconMap: Record<string, React.ReactNode> = {
  avgSentiment: <FaceIcon />,
  avgQaScore: <RulerHorizontalIcon />,
  avgQaScoreRoundRange: <RulerHorizontalIcon />,
  avgQuackMessageScoreRoundRange: <RulerHorizontalIcon />,
};

export const filterMenuItems: FilterMenuSubMenu[] = [
  {
    field: "avgSentiment",
    options: [
      { label: "Positive", value: { avgSentiment: { gte: 0.3 } } },
      {
        label: "Neutral",
        value: {
          and: [{ avgSentiment: { gt: -0.3 } }, { avgSentiment: { lt: 0.3 } }],
        },
      },
      { label: "Negative", value: { avgSentiment: { lte: -0.3 } } },
    ],
  },
  {
    field: "avgQaScore",
    options: [
      { label: "High", value: { avgQaScore: { gt: 4.5 } } },
      {
        label: "Medium",
        value: {
          and: [{ avgQaScore: { gte: 3 } }, { avgQaScore: { lte: 4.5 } }],
        },
      },
      { label: "Low", value: { avgQaScore: { lt: 3 } } },
    ],
  },
  {
    field: "category",
    options: [
      {
        label: "Bug",
        value: { category: { eq: "bug" } },
        icon: <ComponentNoneIcon />,
      },
      {
        label: "Feedback",
        value: { category: { eq: "feedback" } },
        icon: <ComponentBooleanIcon />,
      },
      {
        label: "Request",
        value: { category: { eq: "general_request" } },
        icon: <ComponentInstanceIcon />,
      },
      {
        label: "Feature Request",
        value: { category: { eq: "feature_request" } },
        icon: <ComponentPlaceholderIcon />,
      },
      {
        label: "Education",
        value: { category: { eq: "information_or_education" } },
        icon: <Half1Icon />,
      },
      {
        label: "Troubleshooting",
        value: { category: { eq: "troubleshooting" } },
        icon: <CommitIcon />,
      },
    ],
  },
];

export const backofficeFilterMenuItems: FilterMenuSubMenu[] = [
  {
    field: "countPublicMessages",
    options: [
      {
        label: "Has Public Messages",
        value: { countPublicMessages: { gt: 0 } },
      },
      {
        label: "No Public Messages",
        value: { countPublicMessages: { eq: 0 } },
      },
    ],
  },
];

export function buildActiveFiltersSearchParams(
  searchParams: URLSearchParams,
  activeFilters: ActiveFilter[]
) {
  activeFilters.forEach((filter) => {
    const { field, value } = filter;
    searchParams.append(field, value.label);
  });
}

export function restoreActiveFiltersFromSearchParams(
  searchParams: URLSearchParams
): ActiveFilter[] {
  const activeFilters: ActiveFilter[] = [];

  for (const [key, value] of searchParams.entries()) {
    const subMenu = filterMenuItems.find(({ field }) => field === key);
    if (!subMenu) {
      activeFilters.push({
        field: key,
        value: { label: value, value: { [key]: { eq: value } } },
      });

      continue;
    }

    const filterMenuItem = subMenu.options.find(({ label }) => label === value);
    if (!filterMenuItem) continue;

    activeFilters.push({ field: key, value: filterMenuItem });
  }

  return activeFilters;
}

// Parse matchRules (Filter object) into activeFilters
export function parseMatchRulesToActiveFilters(
  matchRules: Record<string, any> | undefined,
  menuItems?: FilterMenuSubMenu[]
): ActiveFilter[] {
  if (!matchRules) return [];

  // Handle the case where matchRules is { and: [] } (empty rules)
  if (
    matchRules.and &&
    Array.isArray(matchRules.and) &&
    matchRules.and.length === 0
  ) {
    return [];
  }

  if (matchRules.and && Array.isArray(matchRules.and)) {
    // Try to match each filter to a menu item
    return matchRules.and.map((f: Record<string, any>) => {
      for (const menu of [...filterMenuItems, ...backofficeFilterMenuItems]) {
        const found = menu.options.find(
          (opt) => JSON.stringify(opt.value) === JSON.stringify(f)
        );
        if (found) return { field: menu.field, value: found };
      }
      // fallback for custom filters
      return {
        field: "custom",
        value: { label: JSON.stringify(f), value: f },
      };
    });
  }

  // Single filter
  for (const menu of [...filterMenuItems, ...backofficeFilterMenuItems]) {
    const found = menu.options.find(
      (opt) => JSON.stringify(opt.value) === JSON.stringify(matchRules)
    );
    if (found) return [{ field: menu.field, value: found }];
  }

  return [
    {
      field: "custom",
      value: { label: JSON.stringify(matchRules), value: matchRules },
    },
  ];
}
